import { OPTIONAL, PENDING } from './required'

/**
 * Typed access to the VITE_ environment.
 *
 * **Nothing here is a build-time gate.** `vite build` bundles modules; it does
 * not execute them, so a throw at module evaluation happens in the browser as
 * a white screen. The build gate is `tracke5:require-env` in `vite.config.ts`,
 * which calls `loadEnv` at config time and throws before a bundle exists. It
 * reads its list from `./required.ts`, and a test asserts that list agrees
 * with this file — so a name cannot be required here without also gating the
 * build there.
 *
 * `required()` below is the runtime backstop for that gate, not a substitute.
 */

type Env = {
  apiBase: string
  firebase: { apiKey: string; authDomain: string; projectId: string; appId: string }
  revenueCatWebKey: string
  /** C3. `undefined` until set: Sentry is simply off. Never throws. */
  sentryDsn: string | undefined
  /** C4. Same contract as `sentryDsn`. */
  measurementId: string | undefined
  plaidEnv: 'sandbox' | 'development' | 'production'
  flags: Record<'reports' | 'insights' | 'assistant' | 'import' | 'flinks', boolean>
}

const PLAID_ENVS = ['sandbox', 'development', 'production'] as const

function required(name: string): string {
  const value = import.meta.env[name] as string | undefined
  if (!value) {
    throw new Error(
      `Missing required env var: ${name}. Set it in .env.<mode> ` +
        `(or .env.<mode>.local). Tests should stub it with vi.stubEnv.`,
    )
  }
  return value
}

/**
 * A credential that does not exist yet. Named so the failure says which one
 * and which credential ticket it belongs to, rather than yielding undefined
 * and surfacing three layers away.
 */
function pending(name: keyof typeof PENDING): string {
  const value = import.meta.env[name] as string | undefined
  if (!value) {
    throw new Error(
      `Env var ${name} is not configured yet (credential ${PENDING[name]}). ` +
        `Add it to the three .env.<mode> files and move it from PENDING to ` +
        `REQUIRED in src/config/required.ts.`,
    )
  }
  return value
}

/** A value whose absence is a supported state: `undefined`, never a throw. */
function optional(name: keyof typeof OPTIONAL): string | undefined {
  const value = import.meta.env[name] as string | undefined
  return value ? value : undefined
}

function plaidEnv(): Env['plaidEnv'] {
  const value = import.meta.env.VITE_PLAID_ENV as string | undefined
  // Validated, not cast — `sandbx` would otherwise typecheck into a lying union.
  if (value && (PLAID_ENVS as readonly string[]).includes(value)) {
    return value as Env['plaidEnv']
  }
  return 'sandbox'
}

const flag = (name: string): boolean => import.meta.env[name] === 'true'

/**
 * Built once and frozen, not rebuilt per read.
 *
 * A getter that allocated on every access would give `env.firebase` a fresh
 * identity each time — and `firebaseAdapter.ts` records that `initializeApp` is
 * "not idempotent across distinct config objects". One reader exists today, so
 * it is latent rather than live, which is exactly the kind of thing that stops
 * being latent quietly. Frozen so the single instance cannot be mutated by one
 * reader under another.
 */
let firebaseCache: Env['firebase'] | null = null

function firebaseConfig(): Env['firebase'] {
  // `required`, not `pending`, since C2 landed — the names moved to REQUIRED in
  // the same commit as the values, so the BUILD now fails on a missing one and
  // this is the runtime backstop rather than the gate.
  firebaseCache ??= Object.freeze({
    apiKey: required('VITE_FIREBASE_API_KEY'),
    authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: required('VITE_FIREBASE_PROJECT_ID'),
    appId: required('VITE_FIREBASE_APP_ID'),
  })
  return firebaseCache
}

/**
 * The `required` fields are eager; the `pending` ones are GETTERS.
 *
 * This object is built at module evaluation, so an eager `pending()` fires for
 * every unresolved credential the moment anything imports `env` — and each one
 * throws. Wiring the Firebase port in `main.tsx` therefore took the whole app
 * down with `VITE_REVENUECAT_WEB_KEY is not configured yet (credential C1)`:
 * a blank page, in the composition root, over a credential that belongs to
 * Phase 7 and has nothing to do with signing in. Measured in a browser against
 * the real dev values, not reasoned about — nothing in the suite sees it,
 * because no test imports this module for its side effect.
 *
 * A getter keeps the failure where it belongs: reading a credential that does
 * not exist throws, and not reading it costs nothing. The eager half stays
 * eager on purpose — a missing REQUIRED var should fail as loudly and as early
 * as possible, and `tracke5:require-env` has already failed the build by then.
 */
export const env: Env = {
  apiBase: required('VITE_API_BASE'),
  get firebase() {
    return firebaseConfig()
  },
  get revenueCatWebKey() {
    return pending('VITE_REVENUECAT_WEB_KEY')
  },
  get sentryDsn() {
    return optional('VITE_SENTRY_DSN')
  },
  get measurementId() {
    return optional('VITE_FIREBASE_MEASUREMENT_ID')
  },
  plaidEnv: plaidEnv(),
  flags: {
    reports: flag('VITE_FLAG_REPORTS'),
    insights: flag('VITE_FLAG_INSIGHTS'),
    assistant: flag('VITE_FLAG_ASSISTANT'),
    import: flag('VITE_FLAG_IMPORT'),
    flinks: flag('VITE_FLAG_FLINKS'),
  },
}
